import type { ProgressView, TodayResponse } from '@xox/shared'
import { Flame, Play, Swords } from 'lucide-react'
import { runStatusLine } from '../../dungeon/copy'
import { ResetCountdown } from '../ResetCountdown'
import { Card, LinkButton } from '../ui'

interface Props {
  today: TodayResponse | null
  progress: ProgressView | undefined
}

/** Today's dungeon at a glance: where you stand, your streak, the reset, one button. */
export function TodayCard({ today, progress }: Props) {
  const run = today?.run ?? null
  const cta =
    run?.status === 'active'
      ? { to: `/dungeon/run/${run.id}`, label: `Resume · floor ${run.floor}`, icon: Play }
      : run
        ? { to: `/dungeon/run/${run.id}`, label: 'Review your run', icon: Play }
        : { to: '/dungeon', label: 'Enter the dungeon', icon: Swords }
  return (
    <Card className="flex flex-col gap-3 bg-[linear-gradient(135deg,var(--poster-dungeon-1),var(--poster-dungeon-2))] text-white ring-0">
      <div>
        <div className="flex items-center justify-between gap-2 text-[11px] font-bold uppercase tracking-widest opacity-80">
          <span>Today's dungeon{today ? ` · ${today.day}` : ''}</span>
          {progress && progress.streak > 0 && (
            <span className="inline-flex items-center gap-1 normal-case tracking-normal text-orange-200">
              <Flame className="size-3.5" aria-hidden /> {progress.streak}-day streak
            </span>
          )}
        </div>
        <h2 className="text-xl font-black leading-tight">
          {run ? runStatusLine(run) : 'One dungeon. One run. Everyone.'}
        </h2>
        {today && (
          <ResetCountdown
            deadlineAt={today.resetAt}
            serverNow={today.serverNow}
            label="New dungeon in"
            className="text-xs opacity-80"
          />
        )}
      </div>
      {today?.carryOver && (
        <LinkButton
          to={`/dungeon/run/${today.carryOver.id}`}
          variant="secondary"
          className="w-full"
        >
          Finish yesterday's run · floor {today.carryOver.floor}
        </LinkButton>
      )}
      {run && run.status !== 'active' && (
        <div className="text-2xl font-black tabular-nums">{run.score} pts</div>
      )}
      <LinkButton to={cta.to} variant={run ? 'secondary' : 'primary'} className="w-full">
        <cta.icon className="size-4" aria-hidden /> {cta.label}
      </LinkButton>
    </Card>
  )
}
